import { MapPin, Monitor, Users, Layers, GraduationCap, Globe, BookOpen } from "lucide-react";
import { formatTeacherLocation } from "@/lib/utils";

interface TeachingDetailsProps {
  teachingAudience: string[];
  curriculumSystems: string[];
  instructionLanguages: string[];
  otherInstructionLanguage?: string | null;
  teachingModes: string[];
  locationDisplayName?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
}

const audienceLabels: Record<string, string> = {
  PRIMARY:       "Grade 1–3",
  UPPER_PRIMARY: "Grade 4–6",
  MIDDLE_SCHOOL: "Grade 7–9",
  GRADE_10:      "Grade 10",
  GRADE_11_12:   "Grade 11–12",
  UNIVERSITY:    "University",
  ADULT:         "Adult Learners",
};

const modeConfig: Record<string, { icon: typeof Monitor; label: string; bg: string; text: string; border: string }> = {
  ONLINE:  { icon: Monitor, label: "Online",  bg: "bg-[hsl(237,52%,31%)]/10", text: "text-[hsl(237,52%,31%)]", border: "border-[hsl(237,52%,31%)]/20" },
  OFFLINE: { icon: Users,   label: "Offline", bg: "bg-slate-100",              text: "text-slate-600",           border: "border-slate-200" },
  HYBRID:  { icon: Layers,  label: "Hybrid",  bg: "bg-[hsl(39,96%,48%)]/10",  text: "text-[hsl(39,72%,38%)]",  border: "border-[hsl(39,96%,48%)]/20" },
};

const chipBase = "text-xs font-semibold px-2.5 py-1 rounded-full bg-[hsl(237,52%,31%)]/8 text-[hsl(237,52%,31%)]";

interface RowProps {
  icon: React.ElementType;
  iconBg: string;
  iconColor: string;
  label: string;
  children: React.ReactNode;
}

function Row({ icon: Icon, iconBg, iconColor, label, children }: RowProps) {
  return (
    <div className="flex items-start gap-4 px-5 py-4">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${iconBg}`}>
        <Icon className={`w-4 h-4 ${iconColor}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">{label}</p>
        {children}
      </div>
    </div>
  );
}

export function TeachingDetails({
  teachingAudience = [],
  curriculumSystems = [],
  instructionLanguages = [],
  otherInstructionLanguage,
  teachingModes = [],
  locationDisplayName,
  city,
  state,
  country,
}: TeachingDetailsProps) {
  const audience  = teachingAudience ?? [];
  const curricula = curriculumSystems ?? [];
  const languages = instructionLanguages ?? [];
  const modes     = teachingModes ?? [];

  const hasLocation = locationDisplayName || city || country;
  const isEmpty =
    audience.length === 0 && curricula.length === 0 &&
    languages.length === 0 && modes.length === 0 && !hasLocation;

  if (isEmpty) return null;

  const audienceChips = audience.map(a => audienceLabels[a] ?? a);
  const languageChips = languages
    .filter(l => l !== "OTHER")
    .concat(languages.includes("OTHER") && otherInstructionLanguage ? [otherInstructionLanguage] : []);
  const locationText = formatTeacherLocation(locationDisplayName, city, state, country);

  return (
    <section>
      <div className="flex items-center gap-3 mb-4">
        <div className="h-5 w-1 rounded-full bg-gradient-to-b from-[hsl(39,96%,48%)] to-[hsl(30,90%,45%)]" />
        <h2 className="text-xl font-bold text-foreground">Teaching Details</h2>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-slate-50 dark:bg-slate-800/50 dark:border-slate-700 overflow-hidden divide-y divide-slate-100 dark:divide-slate-700">

        {modes.length > 0 && (
          <Row icon={Monitor} iconBg="bg-[hsl(237,52%,31%)]/10" iconColor="text-[hsl(237,52%,31%)]" label="Teaching Mode">
            <div className="flex flex-wrap gap-2">
              {modes.map(m => {
                const cfg = modeConfig[m];
                const Icon = cfg?.icon ?? Monitor;
                return (
                  <span key={m} className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full border ${cfg?.bg ?? "bg-muted"} ${cfg?.text ?? "text-foreground"} ${cfg?.border ?? "border-border"}`}>
                    <Icon className="w-3.5 h-3.5 shrink-0" />
                    {cfg?.label ?? m}
                  </span>
                );
              })}
            </div>
          </Row>
        )}

        {hasLocation && (
          <Row icon={MapPin} iconBg="bg-rose-50" iconColor="text-rose-500" label="Location">
            <p className="text-sm font-medium text-slate-700">{locationText}</p>
          </Row>
        )}

        {audienceChips.length > 0 && (
          <Row icon={GraduationCap} iconBg="bg-[hsl(39,96%,48%)]/10" iconColor="text-[hsl(39,72%,38%)]" label="Teaches">
            <div className="flex flex-wrap gap-1.5">
              {audienceChips.map((a, i) => <span key={i} className={chipBase}>{a}</span>)}
            </div>
          </Row>
        )}

        {languageChips.length > 0 && (
          <Row icon={Globe} iconBg="bg-[hsl(237,52%,31%)]/10" iconColor="text-[hsl(237,52%,31%)]" label="Instruction Language">
            <div className="flex flex-wrap gap-1.5">
              {languageChips.map((l, i) => <span key={i} className={chipBase}>{l}</span>)}
            </div>
          </Row>
        )}

        {curricula.length > 0 && (
          <Row icon={BookOpen} iconBg="bg-[hsl(237,52%,31%)]/10" iconColor="text-[hsl(237,52%,31%)]" label="Curriculum">
            <div className="flex flex-wrap gap-1.5">
              {curricula.map((c, i) => <span key={i} className={chipBase}>{c}</span>)}
            </div>
          </Row>
        )}

      </div>
    </section>
  );
}
